import { useState, useEffect, useMemo } from 'react'
import styled, { keyframes, css } from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabase'
import { ecrire, lire } from './erreurs'
import { synchroniserCommande } from './tournees'
import { montant } from './format'
import { ORDER_STATUS, camionsDe, echeance, CAPACITE_CAMION } from './carnetUtils'
import {
  t, Stagger, Panel, PageHead, PageTitle, PageSub,
  StatGrid, Tile, ChipBar, Chip, Btn, Loading, Empty, Muted, Pill, useUI,
} from './ui'

/* ── Dates ───────────────────────────────────────────────────
   Tout se fait en heure locale : une livraison du 7 août doit
   tomber le 7 août, quel que soit le fuseau du navigateur. */
const minuit = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x }
const cle = (d) => {
  const x = minuit(d)
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`
}
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
const JOURS = ['lun', 'mar', 'mer', 'jeu', 'ven', 'sam', 'dim']

/* Grille du mois, du lundi précédent au dimanche suivant : un mois
   commençant un jeudi laisserait sinon un trou en tête de grille. */
function grilleDuMois(annee, mois) {
  const premier = new Date(annee, mois, 1)
  const debut = minuit(premier)
  debut.setDate(debut.getDate() - ((premier.getDay() + 6) % 7))
  return Array.from({ length: 42 }, (_, i) => {
    const j = new Date(debut)
    j.setDate(debut.getDate() + i)
    return j
  })
}

/* ── Habillage ───────────────────────────────────────────────
   Le thème reste celui de l'atelier ; la modernité vient de la
   grille et du mouvement, pas d'un changement de palette. */
const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(15,92,77,0.28); }
  50%      { box-shadow: 0 0 0 5px rgba(15,92,77,0); }
`
const Barre = styled.div`
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  margin-bottom: 16px;
`
const MoisTitre = styled.div`
  font-family: ${t.serif}; font-size: 1.16rem; font-weight: 650;
  letter-spacing: -0.03em; color: ${t.text};
  min-width: 168px; text-transform: capitalize;
`
const Fleche = styled.button`
  width: 32px; height: 32px; border-radius: ${t.rSm};
  border: 1px solid ${t.line}; background: ${t.surface};
  color: ${t.textSoft}; cursor: pointer; font-size: 0.9rem;
  transition: all 0.14s;
  &:hover { border-color: ${t.gold}; color: ${t.gold}; background: ${t.goldWash}; }
`

const Grille = styled.div`
  display: grid; grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 1px; background: ${t.line};
  border: 1px solid ${t.line}; border-radius: ${t.rSm};
  overflow: hidden;
`
const EnteteJour = styled.div`
  background: ${t.surfaceAlt}; padding: 7px 8px;
  font-family: ${t.font}; font-size: 0.6rem; font-weight: 600;
  letter-spacing: 0.08em; text-transform: uppercase; color: ${t.textFaint};
  text-align: center;
`
const Case = styled.div`
  background: ${(p) => (p.$cible ? t.goldWash : p.$horsMois ? t.surfaceSunk : t.surface)};
  outline: ${(p) => (p.$cible ? `2px solid ${t.gold}` : 'none')};
  outline-offset: -2px;
  min-height: 96px; padding: 6px 6px 8px;
  display: flex; flex-direction: column; gap: 4px;
  opacity: ${(p) => (p.$horsMois ? 0.55 : 1)};
  transition: background 0.14s;
  &:hover { background: ${(p) => (p.$horsMois ? t.surfaceSunk : t.surfaceAlt)}; }

  @media (max-width: 720px) { min-height: 72px; padding: 4px; }
`
const NumJour = styled.div`
  display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 50%;
  font-family: ${t.font}; font-size: 0.72rem; font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$today ? '#fff' : t.textSoft)};
  background: ${(p) => (p.$today ? t.gold : 'transparent')};
  /* Le helper css est obligatoire : un keyframes interpolé dans une
     chaîne brute ne s'injecte pas et lève une erreur à l'exécution. */
  ${(p) => p.$today && css`animation: ${pulse} 2.4s ease-out infinite;`}
`
/* Une livraison dans la grille : le client d'abord, c'est ce qu'on
   cherche des yeux ; le tonnage ensuite. */
const Vignette = styled.button`
  width: 100%; text-align: left; cursor: pointer;
  padding: 3px 6px; border-radius: ${t.rXs};
  border: 1px solid ${(p) => p.$bord};
  background: ${(p) => p.$fond};
  font-family: ${t.font}; font-size: 0.68rem; line-height: 1.3;
  color: ${(p) => p.$teinte};
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  transition: transform 0.12s, filter 0.12s, opacity 0.12s;
  opacity: ${(p) => (p.$saisi ? 0.4 : 1)};
  cursor: grab;
  &:active { cursor: grabbing; }
  &:hover { transform: translateX(2px); filter: brightness(0.97); }
  b { font-weight: 640; }
`
const Jauge = styled.div`
  height: 3px; border-radius: 2px; margin-top: auto;
  background: ${t.lineSoft};
  span {
    display: block; height: 100%; border-radius: 2px;
    background: ${(p) => (p.$plein ? t.amber : t.gold)};
    width: ${(p) => Math.min(100, p.$pct)}%;
    transition: width 0.3s;
  }
`

/* ── À caler ─────────────────────────────────────────────────
   Bandeau des livraisons vendues sans jour fixé. Il sert de réserve :
   on y puise pour remplir le calendrier, et on peut y remettre une
   livraison dont la date se libère. */
const Reserve = styled.div`
  border: 1px dashed ${(p) => (p.$cible ? t.gold : t.line)};
  background: ${(p) => (p.$cible ? t.goldWash : t.surfaceAlt)};
  border-radius: ${t.rSm};
  padding: 11px 13px; margin-bottom: 16px;
  transition: background 0.14s, border-color 0.14s;
`
const ReserveTitre = styled.div`
  display: flex; align-items: center; gap: 8px;
  font-family: ${t.font}; font-size: 0.63rem; font-weight: 600;
  letter-spacing: 0.08em; text-transform: uppercase; color: ${t.textFaint};
  margin-bottom: 9px;
`
const Bulles = styled.div`
  display: flex; gap: 8px; flex-wrap: wrap;
`
const Bulle = styled.div`
  display: flex; flex-direction: column; gap: 1px;
  padding: 7px 11px; border-radius: ${t.rSm};
  background: ${t.surface};
  border: 1px solid ${(p) => (p.$saisi ? t.gold : '#CFE6D8')};
  font-family: ${t.font}; cursor: grab;
  opacity: ${(p) => (p.$saisi ? 0.4 : 1)};
  transition: transform 0.12s, border-color 0.14s, opacity 0.12s;
  &:active { cursor: grabbing; }
  &:hover { transform: translateY(-1px); border-color: ${t.gold}; }

  b { font-size: 0.82rem; font-weight: 640; color: ${t.text}; }
  span { font-size: 0.71rem; color: ${t.textSoft}; font-variant-numeric: tabular-nums; }
`

/* ── Agenda ── */
const JourBloc = styled.div`
  display: grid; grid-template-columns: 92px minmax(0, 1fr);
  gap: 14px; padding: 12px 0;
  border-bottom: 1px solid ${t.lineSoft};
  &:last-child { border-bottom: none; }
  @media (max-width: 620px) { grid-template-columns: 1fr; gap: 6px; }
`
const JourDate = styled.div`
  font-family: ${t.font}; font-size: 0.76rem; color: ${t.textSoft};
  b { display: block; font-family: ${t.serif}; font-size: 1.28rem;
      font-weight: 650; color: ${t.text}; letter-spacing: -0.03em; }
`
/* Le jeton n'est pas dans le bundle : l'admin est protégé par un mot de
   passe, mais son JavaScript reste public. On explique où le prendre
   plutôt que de l'exposer. */
const Abonnement = styled.div`
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  padding: 11px 14px; margin-top: 16px;
  border: 1px dashed ${t.line}; border-radius: ${t.rSm};
  background: ${t.surfaceAlt};
  code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.72rem; color: ${t.gold};
    background: ${t.goldWash}; padding: 2px 7px; border-radius: ${t.rXs};
    word-break: break-all;
  }
`
const Course = styled.button`
  width: 100%; text-align: left; cursor: pointer;
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  padding: 9px 12px; margin-bottom: 6px;
  border-radius: ${t.rSm}; border: 1px solid ${t.line};
  background: ${t.surface}; font-family: ${t.font};
  transition: border-color 0.14s, transform 0.12s;
  &:hover { border-color: ${t.gold}; transform: translateX(2px); }
  &:last-child { margin-bottom: 0; }
`
const CourseNom = styled.span`
  font-size: 0.86rem; font-weight: 600; color: ${t.text};
`
const CourseInfo = styled.span`
  font-size: 0.76rem; color: ${t.textSoft};
  font-variant-numeric: tabular-nums;
`

/* Statuts retenus : ce qui est à venir ou vient d'être fait. */
const TEINTES = {
  scheduled: [t.blueWash, t.blue, '#D3E4F3'],
  validated: [t.greenWash, t.green, '#CFE6D8'],
  delivered: [t.surfaceSunk, t.textSoft, '#E6E8E3'],
  sent: [t.amberWash, t.amber, '#F0DFC2'],
}

export default function Planning() {
  const navigate = useNavigate()
  const { toast } = useUI()
  const [commandes, setCommandes] = useState([])
  const [loading, setLoading] = useState(true)
  const [curseur, setCurseur] = useState(() => minuit(new Date()))
  const [vue, setVue] = useState('mois')
  const [saisi, setSaisi] = useState(null)   // commande en cours de déplacement
  const [cible, setCible] = useState(null)   // journée survolée
  const [filtre, setFiltre] = useState('avenir')

  useEffect(() => {
    (async () => {
      const [commandes, tournees] = await Promise.all([
        lire(
          supabase.from('orders')
            .select('id, date_livraison, status, type, poids_total_t, nb_camions_total, ca_total_chf, lignes, clients(name, canton, localite)')
            .in('status', ['validated', 'scheduled', 'delivered'])
            .order('date_livraison'),
          toast, 'chargement du planning',
        ),
        lire(supabase.from('tournees').select('*'), toast, 'chargement des tournées'),
      ])
      if (!commandes) return setLoading(false)

      /* Une commande découpée en tournées ne se place plus elle-même au
         calendrier : ce sont ses camions qui s'y placent, chacun avec sa
         date. La faire figurer aussi la compterait deux fois. */
      const parCommande = {}
      ;(tournees || []).forEach((t) => { (parCommande[t.order_id] ||= []).push(t) })

      const items = []
      commandes.forEach((o) => {
        const camions = parCommande[o.id]
        if (!camions?.length) return items.push({ ...o, tournee: null })
        camions.sort((a, b) => a.rang - b.rang).forEach((t) => items.push({
          ...o,
          id: t.id,                 // c'est la tournée qu'on déplace, pas la commande
          commandeId: o.id,
          tournee: t,
          date_livraison: t.date_livraison,
          poids_total_t: t.poids_t ?? o.poids_total_t,
          status: t.statut === 'livree' ? 'delivered' : o.status,
          lignes: [{ produit: t.produits }],
          rang: t.rang,
          surTotal: camions.length,
        }))
      })
      setCommandes(items)
      setLoading(false)
    })()
  }, [toast])

  /* Livraisons rangées par jour, une seule fois pour les deux vues. */
  const parJour = useMemo(() => {
    const m = {}
    commandes
      .filter((o) => o.date_livraison)
      .filter((o) => (filtre === 'tout' ? true : o.status !== 'delivered'))
      .forEach((o) => {
        const k = cle(o.date_livraison)
        ;(m[k] = m[k] || []).push(o)
      })
    return m
  }, [commandes, filtre])

  const aujourdhui = minuit(new Date())
  /* Vendu mais sans jour fixé : la marchandise est engagée, il reste à
     la caler. C'est la file de travail du planning. */
  const nonPlanifiees = commandes.filter((o) => !o.date_livraison && o.status !== 'delivered')

  const aVenir = commandes
    .filter((o) => o.date_livraison && o.status !== 'delivered' && minuit(o.date_livraison) >= aujourdhui)
    .sort((a, b) => new Date(a.date_livraison) - new Date(b.date_livraison))

  const tonnageAVenir = aVenir.reduce((s, o) => s + (o.poids_total_t || 0), 0)
  const camionsAVenir = aVenir.reduce((s, o) => s + camionsDe(o), 0)
  const enRetard = commandes.filter((o) =>
    o.date_livraison && o.status !== 'delivered' && minuit(o.date_livraison) < aujourdhui)

  /* Déplacer une livraison. La commande porte la date, quel que soit
     son type — devis, pro forma ou facture sont le même enregistrement,
     une seule écriture suffit donc. */
  const deplacer = async (id, jourCible) => {
    const commande = commandes.find((o) => o.id === id)
    if (!commande) return
    const actuelle = commande.date_livraison ? cle(commande.date_livraison) : null
    if (actuelle === jourCible) return

    const avant = commande.date_livraison
    /* Mise à jour immédiate à l'écran : un glisser-déposer qui attend la
       base donne l'impression d'avoir raté sa cible. Rétabli si l'écriture
       échoue. */
    setCommandes((prev) => prev.map((o) => (o.id === id ? { ...o, date_livraison: jourCible } : o)))

    const ok = await ecrire(
      commande.tournee
        ? supabase.from('tournees').update({ date_livraison: jourCible }).eq('id', commande.tournee.id)
        : supabase.from('orders').update({ date_livraison: jourCible }).eq('id', id),
      toast, 'changement de date',
    )
    if (!ok) {
      setCommandes((prev) => prev.map((o) => (o.id === id ? { ...o, date_livraison: avant } : o)))
      return
    }
    /* La commande recopie la date de sa première tournée : le carnet et
       les statistiques la lisent encore. */
    if (commande.tournee) await synchroniserCommande(commande.commandeId)
    const qui = commande.clients?.name || 'Livraison'
    if (!jourCible) return toast(`${qui} retirée du calendrier`)
    const j = new Date(`${jourCible}T00:00:00`)
    toast(`${qui} placée au ${j.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long' })}`)
  }

  const jours = grilleDuMois(curseur.getFullYear(), curseur.getMonth())
  const moisCourant = curseur.getMonth()

  const decaler = (n) => setCurseur((d) => new Date(d.getFullYear(), d.getMonth() + n, 1))

  if (loading) return <Loading rows={4} h="90px" />

  return (
    <>
      <PageHead>
        <div>
          <PageTitle>Planning des livraisons</PageTitle>
          <PageSub>
            {aVenir.length} livraison(s) à venir · {tonnageAVenir.toFixed(1)} T · {camionsAVenir} camion(s)
          </PageSub>
        </div>
        <Btn $variant="ghost" onClick={() => setCurseur(minuit(new Date()))}>Aujourd'hui</Btn>
      </PageHead>

      <StatGrid style={{ marginBottom: 18 }}>
        <Tile i={0} label="Prochaine livraison"
          value={aVenir[0] ? new Date(aVenir[0].date_livraison).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '—'}
          hint={aVenir[0] ? (aVenir[0].clients?.name || 'client') : 'rien de planifié'} tone="gold" />
        <Tile i={1} label="À venir" value={`${aVenir.length}`}
          hint={`${tonnageAVenir.toFixed(1)} T à charger`} tone="blue" />
        <Tile i={2} label="Camions" value={`${camionsAVenir}`}
          hint={`${CAPACITE_CAMION} T par remorque`} tone="teal" />
        <Tile i={3} label="En retard" value={`${enRetard.length}`}
          hint={enRetard.length ? 'à replanifier ou clôturer' : 'rien en souffrance'}
          tone={enRetard.length ? 'red' : 'neutral'} />
      </StatGrid>

      <Barre>
        <Fleche onClick={() => decaler(-1)} title="Mois précédent">‹</Fleche>
        <MoisTitre>{MOIS[curseur.getMonth()]} {curseur.getFullYear()}</MoisTitre>
        <Fleche onClick={() => decaler(1)} title="Mois suivant">›</Fleche>

        <ChipBar style={{ marginLeft: 'auto' }}>
          <Chip $on={vue === 'mois'} onClick={() => setVue('mois')}>📅 Mois</Chip>
          <Chip $on={vue === 'agenda'} onClick={() => setVue('agenda')}>📋 Agenda</Chip>
          <Chip $on={filtre === 'tout'} onClick={() => setFiltre(filtre === 'tout' ? 'avenir' : 'tout')}>
            {filtre === 'tout' ? '👁 Livrées incluses' : '👁 Livrées masquées'}
          </Chip>
        </ChipBar>
      </Barre>

      {vue === 'mois' && (
        <Stagger>
          {(nonPlanifiees.length > 0 || saisi) && (
            <Reserve $cible={cible === 'reserve'}
              onDragOver={(e) => { e.preventDefault(); setCible('reserve') }}
              onDragLeave={() => setCible((c) => (c === 'reserve' ? null : c))}
              onDrop={(e) => {
                e.preventDefault()
                setCible(null)
                const id = e.dataTransfer.getData('text/plain')
                if (id) deplacer(id, null)
              }}>
              <ReserveTitre>
                À caler — vendu, sans date
                <Pill $tone="green">{nonPlanifiees.length}</Pill>
                <span style={{ marginLeft: 'auto', textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>
                  glissez vers un jour du calendrier, ou déposez ici pour retirer une date
                </span>
              </ReserveTitre>
              {nonPlanifiees.length === 0 ? (
                <Muted style={{ fontSize: '0.76rem' }}>Tout est calé.</Muted>
              ) : (
                <Bulles>
                  {nonPlanifiees.map((o) => (
                    <Bulle key={o.id} $saisi={saisi === o.id}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', o.id)
                        e.dataTransfer.effectAllowed = 'move'
                        setSaisi(o.id)
                      }}
                      onDragEnd={() => { setSaisi(null); setCible(null) }}
                      onClick={() => { if (!saisi) navigate(`/admin/carnet/${o.id}`) }}
                      title={`${o.clients?.name || 'client'} — ${(o.lignes || []).map((l) => l.produit).join(', ')}`}>
                      <b>{o.clients?.name || 'client'}</b>
                      <span>
                        {o.poids_total_t || 0} T · {camionsDe(o)} camion(s)
                        {o.clients?.canton ? ` · ${o.clients.canton}` : ''}
                      </span>
                    </Bulle>
                  ))}
                </Bulles>
              )}
            </Reserve>
          )}

          <Grille>
            {JOURS.map((j) => <EnteteJour key={j}>{j}</EnteteJour>)}
            {jours.map((j) => {
              const k = cle(j)
              const courses = parJour[k] || []
              const tonnage = courses.reduce((s, o) => s + (o.poids_total_t || 0), 0)
              return (
                <Case key={k} $horsMois={j.getMonth() !== moisCourant} $cible={cible === k}
                  onDragOver={(e) => { e.preventDefault(); setCible(k) }}
                  onDragLeave={() => setCible((c) => (c === k ? null : c))}
                  onDrop={(e) => {
                    e.preventDefault()
                    setCible(null)
                    const id = e.dataTransfer.getData('text/plain')
                    if (id) deplacer(id, k)
                  }}>
                  <NumJour $today={k === cle(aujourdhui)}>{j.getDate()}</NumJour>
                  {courses.slice(0, 3).map((o) => {
                    const [fond, teinte, bord] = TEINTES[o.status] || TEINTES.sent
                    return (
                      <Vignette key={o.id} $fond={fond} $teinte={teinte} $bord={bord}
                        $saisi={saisi === o.id}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', o.id)
                          e.dataTransfer.effectAllowed = 'move'
                          setSaisi(o.id)
                        }}
                        onDragEnd={() => { setSaisi(null); setCible(null) }}
                        /* Un glisser se termine parfois par un clic parasite :
                           on n'ouvre la commande que si rien n'a été déplacé. */
                        onClick={() => { if (!saisi) navigate(`/admin/carnet/${o.id}`) }}
                        title={`${o.clients?.name || 'client'} — ${o.poids_total_t || 0} T · glissez pour changer de jour`}>
                        <b>{o.clients?.name || 'client'}</b>
                        {o.tournee ? ` · ${o.rang}/${o.surTotal}` : ''} · {o.poids_total_t || 0} T
                      </Vignette>
                    )
                  })}
                  {courses.length > 3 && (
                    <Muted style={{ fontSize: '0.66rem' }}>+{courses.length - 3} autre(s)</Muted>
                  )}
                  {tonnage > 0 && (
                    <Jauge $pct={(tonnage / CAPACITE_CAMION) * 100} $plein={tonnage > CAPACITE_CAMION}
                      title={`${tonnage.toFixed(1)} T ce jour`}>
                      <span />
                    </Jauge>
                  )}
                </Case>
              )
            })}
          </Grille>
          <Muted style={{ fontSize: '0.72rem', marginTop: 10, display: 'block' }}>
            Glissez une livraison d'un jour à l'autre pour la replanifier — la date est
            enregistrée aussitôt sur la commande, quel que soit son type. La barre au bas
            d'une journée compare son tonnage à la capacité d'une remorque
            ({CAPACITE_CAMION} T) ; elle vire à l'ambre au-delà, signe qu'il faut un second camion.
          </Muted>
        </Stagger>
      )}

      <Abonnement>
        <span style={{ fontSize: '1.05rem' }}>📲</span>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontFamily: t.font, fontSize: '0.82rem', fontWeight: 600, color: t.text }}>
            Retrouver ces livraisons dans Calendrier
          </div>
          <Muted style={{ fontSize: '0.75rem', display: 'block', marginTop: 3 }}>
            Réglages → Calendrier → Comptes → Ajouter un compte → Autre → Calendrier avec abonnement,
            puis coller <code>https://achatpaille.com/api/calendar.ics?token=VOTRE_JETON</code>.
            Le jeton est la variable <code>CALENDAR_TOKEN</code> définie dans Vercel ; il tient lieu
            de mot de passe, ne le partagez pas.
          </Muted>
        </div>
      </Abonnement>

      {vue === 'agenda' && (
        <Stagger>
          <Panel>
            {Object.keys(parJour).sort().filter((k) => k >= cle(aujourdhui)).length === 0 ? (
              <Empty icon="🚛" title="Rien de planifié">
                Les livraisons apparaissent ici dès qu'une date leur est fixée depuis le carnet.
              </Empty>
            ) : (
              Object.keys(parJour).sort()
                .filter((k) => k >= cle(aujourdhui))
                .map((k) => {
                  const j = new Date(`${k}T00:00:00`)
                  const courses = parJour[k]
                  const ech = echeance(k)
                  return (
                    <JourBloc key={k}>
                      <JourDate>
                        <b>{j.getDate()}</b>
                        {j.toLocaleDateString('fr-FR', { weekday: 'long' })}
                        <div style={{ fontSize: '0.7rem', color: t.textFaint }}>
                          {ech?.texte}
                        </div>
                      </JourDate>
                      <div>
                        {courses.map((o) => (
                          <Course key={o.id} onClick={() => navigate(`/admin/carnet/${o.id}`)}>
                            <CourseNom>{o.clients?.name || 'client'}</CourseNom>
                            <Pill $tone={ORDER_STATUS[o.status]?.tone}>
                              {ORDER_STATUS[o.status]?.label.replace(/^\S+\s/, '') || o.status}
                            </Pill>
                            <CourseInfo>
                              {o.poids_total_t || 0} T · {camionsDe(o)} camion(s)
                              {o.clients?.canton ? ` · ${o.clients.canton}` : ''}
                            </CourseInfo>
                            <CourseInfo style={{ marginLeft: 'auto', color: t.green, fontWeight: 600 }}>
                              {montant(o.ca_total_chf)}
                            </CourseInfo>
                          </Course>
                        ))}
                      </div>
                    </JourBloc>
                  )
                })
            )}
          </Panel>
        </Stagger>
      )}
    </>
  )
}
